import { useEffect, useMemo, useState } from 'react';
import { TournamentTournament } from '@/api';

export type TTournamentsFilters = {
    year?: string[];
    title: string[];
    location?: number[];
    offset?: number;
    limit: number;
    order?: Array<'asc' | 'desc'>;
    status?: ('scheduled' | 'processing' | 'active' | 'ended')[];
    showIgnored?: boolean;
};

export type GroupedData = {
    yearMonth: string;
    events: TournamentTournament[];
};

export type Tabs = 'Past' | 'Upcoming';

const getGroupedData = (data: TournamentTournament[], isPast?: boolean) => {
    if (data) {
        const groupedData: GroupedData[] = data.reduce((acc: GroupedData[], item) => {
            const yearMonth = item.startDate?.slice(0, 7); // Формат YYYY-MM

            if (!yearMonth) return acc;
            const yearMonthEntry = acc.find((entry) => entry.yearMonth === yearMonth);

            if (yearMonthEntry) {
                yearMonthEntry.events.push({ ...item });
            } else {
                acc.push({
                    yearMonth: yearMonth,
                    events: [item],
                });
            }
            return acc;
        }, []);

        const order: Record<NonNullable<TournamentTournament['status']>, number> = {
            active: 1,
            processing: 2,
            scheduled: 2,
            ended: 2,
        };

        groupedData.forEach((el) =>
            el.events.sort((a, b) => (a.status && b.status ? order[a.status] - order[b.status] : 0))
        );

        groupedData.sort((a, b) =>
            isPast ? b.yearMonth.localeCompare(a.yearMonth) : a.yearMonth.localeCompare(b.yearMonth)
        );

        return groupedData;
    }
};

const urlParams = new URLSearchParams(window.location.search);

// Извлекаем параметры
const parsedFilters: Partial<TTournamentsFilters> = {};

if (urlParams.has('year')) {
    parsedFilters.year = Array.from(urlParams.getAll('year'));
}

if (urlParams.has('title')) {
    parsedFilters.title = Array.from(urlParams.getAll('title'));
}

if (urlParams.has('location')) {
    parsedFilters.location = Array.from(urlParams.getAll('location')).map(Number);
}

export const useTournamentList = () => {
    const [tournamentList, setTournamentList] = useState<TournamentTournament[]>([]);
    const [tournamentsCount, setTournamentsCount] = useState<number>();
    const [previewTournaments, setPreviewTournaments] = useState<TournamentTournament[]>([]);
    const [year, setYear] = useState<string[]>(parsedFilters.year || []);
    const [title, setTitle] = useState<string[]>(parsedFilters.title || []);
    const [location, setLocation] = useState<number[]>(parsedFilters.location || []);
    const [offset, setOffset] = useState<number>(0);
    const [isPast, setIsPast] = useState<boolean>(true);
    const [groupedTournament, setGroupedTournament] = useState<GroupedData[]>([]);
    const [isAdminMode, setIsAdminMode] = useState(false);
    const [pinId, setPinId] = useState<string>('');

    const [isLoading, setIsLoading] = useState<boolean>(false);

    const [optionsCities, setOptionsCities] = useState<
        {
            value: string;
            label: string;
        }[]
    >();

    const allFilters: TTournamentsFilters = useMemo(
        () => ({
            year,
            title,
            location,
            offset,
            limit: 50,
            status: isPast ? ['active', 'ended'] : ['active', 'scheduled'],
            sort: ['start_date', 'status', 'num_players', 'num_registered'],
            order: isPast ? ['desc', 'asc', 'desc', 'desc'] : ['asc', 'desc', 'desc', 'desc'],
            showIgnored: isAdminMode,
        }),
        [year, title, location, offset, isPast, isAdminMode]
    );

    useEffect(() => {
        const groupedData = getGroupedData(tournamentList, isPast);
        if (!groupedData) return;
        setGroupedTournament(groupedData);
    }, [tournamentList, isPast]);

    return {
        tournamentList,
        allFilters,
        optionsCities,
        isLoading,
        tournamentsCount,
        previewTournaments,
        offset,
        groupedTournament: groupedTournament,
        title,
        isPast,
        isAdminMode,
        pinId,

        setTournamentList,
        setYear,
        setTitle,
        setLocation,
        setOptionsCities,
        setIsLoading,
        setTournamentsCount,
        setPreviewTournaments,
        setOffset,
        setIsPast,
        setIsAdminMode,
        setPinId,
    };
};
